import { createFileRoute } from '@tanstack/react-router'

import type { AccountState } from '@/components/klient/prehled'
import {
  AccountStrip,
  ApprovalBanner,
  FirstShootCard,
  FirstWeekPlan,
  MenuPrompt,
  NextShootCard,
  NotificationRow,
  OpportunityStrip,
  PostCard,
  VenueHeader,
} from '@/components/klient/prehled'
import { DayRailSection, PromoPanel, RatingSheet, SectionLabel } from '@/components/shared'
import { PHOTOGRAPHER_SUBJECT, PICKED, RATING_QUEUE, REASONS } from '@/mocks/klient/hodnoceni'
import {
  FEED_DAYS,
  FIRST_SHOOT,
  FIRST_WEEK,
  FIRST_WEEK_NOTIFICATIONS,
  HAS_LIVE_STORY,
  MENU_PROMPT,
  NEXT_SHOOT,
  NOTIFICATIONS,
  OPPORTUNITIES,
  PAYMENT_DUE,
  SETUP_DUE,
  TODAY,
  VENUE_AVATAR,
  VENUE_NAME,
} from '@/mocks/klient/prehled'

/**
 * The overview's default is the running service. Everything the client sees
 * before it gets there lives in the URL:
 *
 * - `prvni-tyden` — the week after onboarding, when every list here is
 *   legitimately empty,
 * - `nedokonceno` — the fortnight after it, while the second wizard run is
 *   still open; the strip names only its nearest step,
 * - `nezaplaceno` — the business half of the same idea.
 */
const STAV_VALUES = ['prvni-tyden', 'nedokonceno', 'nezaplaceno'] as const
type Stav = (typeof STAV_VALUES)[number]
type PrehledSearch = { stav?: Stav; info?: 'hodnoceni'; hvezdy?: number }

const STRIP_STATES: Partial<Record<Stav, AccountState[]>> = {
  nezaplaceno: [PAYMENT_DUE],
  nedokonceno: [SETUP_DUE],
}

export const Route = createFileRoute('/klient/prehled')({
  validateSearch: (search: Record<string, unknown>): PrehledSearch => ({
    ...(STAV_VALUES.includes(search.stav as Stav) ? { stav: search.stav as Stav } : {}),
    ...(search.info === 'hodnoceni' ? { info: 'hodnoceni' as const } : {}),
    ...(Number(search.hvezdy) >= 1 && Number(search.hvezdy) <= 5
      ? { hvezdy: Number(search.hvezdy) }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Přehled · Aplikace Bitez' }] }),
  component: KlientPrehled,
})

const STAGGER_STEP_MS = 90

function KlientPrehled() {
  const { stav, info, hvezdy } = Route.useSearch()
  const firstWeek = stav === 'prvni-tyden'
  const stripStates = stav ? STRIP_STATES[stav] : undefined

  const notifications = firstWeek ? FIRST_WEEK_NOTIFICATIONS : NOTIFICATIONS
  const unreadCount = notifications.filter((notification) => notification.unread).length
  let postIndex = 0

  return (
    <div className="px-4 pt-6 pb-6">
      <VenueHeader
        name={VENUE_NAME}
        avatar={VENUE_AVATAR}
        date={TODAY}
        unreadCount={unreadCount}
        /* Nothing of theirs has been published yet, so no story is running */
        hasLiveStory={firstWeek ? false : HAS_LIVE_STORY}
      />

      {/* Said once, at the top, and never more than one at a time — the account
          catching up is the frame for everything under it, not an alarm. */}
      {stripStates && (
        <div className="animate-rise-up mt-5">
          <AccountStrip states={stripStates} />
        </div>
      )}

      {firstWeek ? (
        <>
          {/* Week one has exactly one thing worth doing, so it is the first
              thing on the screen and it has a face. */}
          <div className="animate-rise-up mt-5">
            <FirstShootCard photo={FIRST_SHOOT.photo} photographer={FIRST_SHOOT.photographer} />
          </div>

          {/* The feed rail, pointed forward: emptiness as anticipation rather
              than three boxes saying "zatím nic". */}
          <section className="animate-rise-up mt-8" style={{ animationDelay: '60ms' }}>
            <SectionLabel>Co se bude dít</SectionLabel>
            <div className="mt-4">
              <FirstWeekPlan steps={FIRST_WEEK} />
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Frameless, no eyebrow — it reads as a line of the header, not a card */}
          <div className="animate-rise-up mt-5">
            <NextShootCard shoot={NEXT_SHOOT} />
          </div>

          <div className="animate-rise-up mt-5" style={{ animationDelay: '60ms' }}>
            <ApprovalBanner count={3} autoApproveIn="26 h" />
          </div>

          {/* Spec §11's one-tap action, and it only exists while the menu is
              missing — a shortcut that is always there is furniture. */}
          <div className="animate-rise-up mt-5" style={{ animationDelay: '90ms' }}>
            <MenuPrompt {...MENU_PROMPT} />
          </div>
        </>
      )}

      <section className="animate-rise-up mt-6" style={{ animationDelay: '120ms' }}>
        <div className="flex items-baseline justify-between gap-2">
          <SectionLabel>Co je nového</SectionLabel>
          <button
            type="button"
            className="cursor-pointer text-[10px] font-bold text-ink/40 transition-colors duration-150 hover:text-brand"
          >
            Vše
          </button>
        </div>
        <div className="mt-3 space-y-3.5">
          {notifications.map((notification) => (
            <NotificationRow key={notification.title} notification={notification} />
          ))}
        </div>
      </section>

      <section className="animate-rise-up mt-7" style={{ animationDelay: '180ms' }}>
        <SectionLabel>Co je před vámi</SectionLabel>
        <p className="mt-1 text-xs text-ink/50">Ať vám neuteče, co se dá dobře prodat.</p>
        <div className="mt-3">
          <OpportunityStrip opportunities={OPPORTUNITIES} />
        </div>
      </section>

      {/* No upsell in week one — they have not yet got what they already pay for. */}
      {!firstWeek && (
        <div className="animate-rise-up mt-7" style={{ animationDelay: '240ms' }}>
          <PromoPanel
            eyebrow="Nabídka pro vás"
            title="Extra focení"
            note="Focení navíc mimo váš balíček · od 1 900 Kč"
          />
        </div>
      )}

      {/* The feed is the past, so in week one there is nothing to show and the
          section is gone rather than empty — the forward rail above already
          says when it will start filling. */}
      {!firstWeek && (
        <section className="mt-8">
          <SectionLabel>Co už vyšlo</SectionLabel>
          <div className="mt-4">
            {FEED_DAYS.map((group, groupIndex) => (
              <DayRailSection
                key={group.date}
                day={group.day}
                date={group.date}
                last={groupIndex === FEED_DAYS.length - 1}
              >
                <div className="space-y-4 pb-6">
                  {group.posts.map((post) => (
                    <PostCard key={post.image} post={post} delay={++postIndex * STAGGER_STEP_MS} />
                  ))}
                </div>
              </DayRailSection>
            ))}
          </div>
        </section>
      )}

      {/* Asked where it will actually be seen, not on a detail nobody opens —
          and dismissable, because a client locked out of their own app at seven
          in the evening is a worse trade than a missing star (spec §9 gives it a
          seven-day window to come back in). */}
      {info === 'hodnoceni' && (
        <RatingSheet
          closeHref="/klient/prehled"
          question="Jak šlo focení?"
          subject={PHOTOGRAPHER_SUBJECT}
          stars={hvezdy}
          reasons={REASONS}
          picked={hvezdy ? PICKED : []}
          notePlaceholder="Co se povedlo, co příště jinak…"
          privacy="Fotograf vaše hodnocení nevidí — čte ho Bára."
          queue={RATING_QUEUE}
          hrefFor={(stars) => `/klient/prehled?info=hodnoceni&hvezdy=${stars}`}
        />
      )}
    </div>
  )
}
